
/**
* UserPanelSuperAdmin
* --------------------
* This component renders the Super Admin contacts panel.
* It supports:
* - Listing master contacts with pagination & infinite scroll
* - Filtering, sorting, searching
* - Bulk actions (delete, transfer owner, bulk edit)
* - Importing contacts, call logs, notes, and tasks
* - Exporting selected contacts with notes
*
* Tech stack:
* - React + TypeScript
* - Redux for global state
* - Axios for API calls
* - Firebase Firestore for notes
* - MUI for responsiveness
*/
import { FunctionComponent, useEffect, useMemo, useState } from "react";
import ApiTopBar from "../../Components/TopBar/ApiTopBar";
import commonStyle from "../common.module.css";
import { connect, useDispatch } from "react-redux";
import OwnerModal from "../../Components/Modals/ChangeOwnerModal/ChangeOwnerModal";
import { datesField, booleanObject, ContactColumnsMaster } from "../../Values/tables";
import Firebase from "firebase/app";
import styles from './UserPanel.module.css'
import Loading from "../../Components/Loading/Loading";
import { getDateImportString } from "../../Values/utils";
import ApiCustomTable from "../../Components/CustomTable/ApiCustomTable";
import BulkEditModal from "../../Components/Modals/BulkEditModal/BulkEditModal";
import ImportCallLogsModal from "../../Components/Modals/ImportModal/ImportCallLogsModal";
import ImportNotesModal from "../../Components/Modals/ImportModal/ImportNotesModal";
import ImportTasksModal from "../../Components/Modals/ImportModal/ImportTasksModal";
import axios from "axios";
import moment from "moment";
import { url } from "../../Values/constants";
import { setFilterObject, setRefreshContacts, showSnackbarAction, updateFilterStatus, setDeleteLeadsStatus } from "../../Redux/actions";
import MasterContactModal from "../../Components/Modals/ImportModal/MasterContactModal";
import DeleteSelectedModal from "../../Components/Modals/DeleteSelectedModal/DeleteSelectedModal";
import { firestore } from "../../Firebase";
import { themeColors } from "../../Components/theme";
import { useMediaQuery, useTheme } from "@mui/material";
import { conatctHeader } from "../../Values/masterHeader";

/** ----------------------
 * Component Props
 * ---------------------- */
type props = {
  history: any;
  user: any;
  contacts: {
    data: any;
  };
  organizationUsers: any;
  role: any;
  teamLeadUsers: any;
  filterObject: any;
  filterSort: any;
  refreshContacts: boolean;
  searchString: string;
  theme: any;
  authToken: any;
  clerFilterStatus: any;
  deleteLeadsStatus: any;
  organizationIdNameData: any;
};

/**
 * Shared mutable helpers
 * NOTE: These persist across renders
 */
let usersList: any[] = [];
let isFinished = false;

/**
 * UserPanelSuperAdmin Component
 */
const UserPanelSuperAdmin: FunctionComponent<props> = ({
  history,
  user,
  contacts,
  organizationUsers,
  role,
  teamLeadUsers,
  filterObject,
  filterSort,
  refreshContacts,
  searchString,
  theme,
  authToken,
  clerFilterStatus,
  deleteLeadsStatus,
  organizationIdNameData
}) => {

  const dispatcher = useDispatch();

  /** Responsive helpers */
  const themeMUI = useTheme();
  const isMobile = useMediaQuery(themeMUI.breakpoints.down("sm"));
  const [showImportModal, setShowImportModal] = useState(false);
  const [callLogsImportModal, setCallLogsImportModal] = useState(false);
  const [notesImportModal, setNotesImportModal] = useState(false);
  const [tasksImportModal, setTasksImportModal] = useState(false);
  const [show, setShow] = useState(false);
  const [filterCount, setFilterCount] = useState<any>({});
  const [deletePopup, setDeletePopop] = useState(false);
  const [showBulkEdit, setShowBulkEdit] = useState(false);
  const [owner, setOwner] = useState(false);
  const [selectedRows, setSelectedRows] = useState<any[]>([]);
  const [selectedRowsData, setSelectedRowsData] = useState<any[]>([]);
  const [transferStatusTrueRows, setTransferStatusTrueRows] = useState<any[]>([]);
  const [filterData, setFilterData] = useState<any[] | undefined>(undefined);
  const [searchedItem, setsearchedItem] = useState("");
  const [columnModal, setColumnModal] = useState(false);
  const [lastPage, setLastPage] = useState(-1);
  const [allContacts, setAllContacts] = useState<any[] | undefined>(undefined);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(100);
  const [load, setLoad] = useState(false);
  const [allLeads, setAllLeads] = useState(false);
  const [apiCallFlag, setApiCallFlag] = useState(false);
  const [totalCounts, setTotalCounts] = useState<any>();
  const [freshFilterValue, setFreshFilterValue] = useState<any>();
  const [clearFilterTable, setClearFilterTable] = useState(false);

  /**
 * Memoized column configuration
 * Prevents unnecessary re-renders
 */
  const contactColumns = useMemo(
    () => ContactColumnsMaster(conatctHeader),
    [conatctHeader]
  );

  /**
 * Toggle table filter reset
 */
  const handleClearFilter = () => {
    setClearFilterTable(prev => !prev);
  };

  /**
 * Core API Call
 * - Handles pagination
 * - Applies filters & sorting
 * - Normalizes date fields
 */
  const callApi = async (
    paginate?: boolean,
    localPageSize?: Number,
    localPage?: Number
  ) => {
    if (isFinished === true) {
      return;
    } else {

      setLoad(true);

      /** Build filter payload */
      const filters: { [key: string]: any[] } = {};

      let feild;

      filters["transfer_status"] = [false];

      Object.keys(filterObject).forEach((item) => {
        if (!filterObject || !filterObject[item] || filterObject[item].length === 0) return;
        if (["is_button_called", "transfer_status", "associate_status", "source_status"].includes(item)) {
          if (filterObject[item].length === 2) {
            filters[item] = [true, false];
          } else {
            filters[item] = filterObject[item][0] === "True" ? [true] : [false];
          }
        } else {
          if (item !== "contact_no") {
            filters[item] = filterObject[item];
          }
        }
      });
      if (searchString === "") {
        feild = "customer_name";
      } else {
        if (searchString.match(/^[0-9]+$/) != null) {
          feild = "contact_no";
        } else {
          feild = "customer_name";
        }
      }

      setFilterCount(filters)

      let records = localPageSize ? localPageSize : pageSize;

      const apiData = {
        page: localPage ? localPage : page + 1,
        searchString: searchString,
        sort: Object.keys(filterSort).length === 0 ? { created_at: "-1" } : filterSort,
        pageSize: localPageSize ? localPageSize : pageSize,
        filter: filters,
      };

      try {
        axios.defaults.headers.common["x-access-token"] = authToken

        const res = await axios.post(url + "/leads/MasterSearch", apiData);

        let data: any[] = res.data;

        const recordsNum = typeof records === "number" ? records : Number(records);

        /** Stop pagination when no more data */
        if (data.length < recordsNum && paginate) {
          isFinished = true;
          setLastPage(page);
        }

        /** Normalize lead data */
        data.forEach((lead) => {
          lead.contactId = lead.Id;
          delete lead["Id"];
          Object.keys(lead).forEach((key) => {
            if (datesField.includes(key) && lead[key] !== "") {
              if (lead[key] !== null) {
                lead[key] = Firebase.firestore.Timestamp.fromDate(
                  moment(lead[key]).toDate()
                );
              } else {
                lead[key] = "";
              }
            }
          });
        });
        if (paginate && filterData) {
          let newData = [...filterData, ...data];
          setFilterData(newData);
        } else {
          setFilterData(data);
        }
        setLoad(false);
      } catch (e) {
        setLoad(false);
      }
    }
  };

  /**
* Recursive function to create a user hierarchy list
* @param email Starting user's email
* @param users List of all users
* @param uid Boolean flag
*/
  const createUsersList = (email: string, users: any[], uid: boolean) => {
    users.map((item: any) => {
      if (usersList.includes(item)) {
        return;
      } else {
        if (item.reporting_to === email && item.status === "ACTIVE") {
          if (!usersList.includes(item)) {
            usersList.push(item);
          }
          createUsersList(item.user_email, users, uid);
        }
      }
    });
  };

  // Update transfer status for selected rows
  useEffect(() => {
    let trueStatusList: any = [];
    selectedRowsData.forEach((row) => {
      Object.keys(row).forEach((item) => {
        if (item === "transfer_status") {
          trueStatusList.push(row[item]);
        }
      });
    });
    setTransferStatusTrueRows(trueStatusList);
  }, [selectedRowsData]);


  useEffect(() => {
    if (user === undefined) {
      setFilterData([]);
      return;
    }
    isFinished = false;
    setLastPage(-1);
    if (page !== 0) {
      setPage(0);
    }
    const filterStageValue = filterObject.stage?.length === 1 && filterObject?.stage?.filter((item: any) => item === "FRESH");
    filterStageValue ? setFreshFilterValue(filterStageValue[0]) : setFreshFilterValue('');

    const isAnyKeyArrayNotEmpty = Object.keys(filterObject).some(key => filterObject[key].length > 0);

    if (isAnyKeyArrayNotEmpty) {
      if ((authToken)) {
        setApiCallFlag(true);
        dispatcher(updateFilterStatus(false));
        callApi(false, pageSize, 1);
      }
    }
    else if (clerFilterStatus && apiCallFlag) {
      callApi(false, pageSize, 1);
    }
    else {
      if ((authToken && !apiCallFlag)) {
        dispatcher(updateFilterStatus(false));
        setApiCallFlag(true);
        callApi(false, pageSize, 1);
      }
    }
  }, [user, filterObject, filterSort, authToken]);

  // Fetch contacts on search
  useEffect(() => {
    if (searchString != "") {
      callApi(false, pageSize, 1);
    }
  }, [searchString]);

  useEffect(() => {
    if (deleteLeadsStatus === true) {
      callApi(false, pageSize, 1);
      dispatcher(setDeleteLeadsStatus(false));
    }
  }, [deleteLeadsStatus]);

  // Pagination effect
  useEffect(() => {
    if (
      filterData &&
      filterData.length <= (page + 1) * pageSize &&
      isFinished === false
    ) {
      if (page === 0) {
        if (authToken) {
          callApi(false, pageSize);
        }
      } else {
        if (authToken) {
          callApi(true, pageSize);
        }
      }
    }
  }, [page, pageSize, authToken]);

  // Refresh contacts effect
  useEffect(() => {
    if (refreshContacts === true) {
      if (authToken) {
        callApi();
        getContactsFilter();
        dispatcher(setRefreshContacts(false));
        const filterStageValue = filterObject.stage?.length === 1 && filterObject?.stage?.filter((item: any) => item === "FRESH");
        filterStageValue ? setFreshFilterValue(filterStageValue[0]) : setFreshFilterValue('');
      }
    }
  }, [refreshContacts, authToken]);

  useEffect(() => {
    if (user === undefined) {
      setFilterData([]);
      return;
    }
    if (authToken) {
      contactCountApi()
    }
  }, [refreshContacts, filterCount, authToken]);

  /**
  * Fetch total contact count for pagination footer
  */
  const contactCountApi = async () => {
    const { reporting_to, team, branch, ...otherFilterCount } = filterCount;
    axios.defaults.headers.common["x-access-token"] = authToken;
    const res = await axios.post(url + "/leads/MasterContactCount", {
      leadFilter: otherFilterCount,
      leadUserFilter: {
        reporting_to,
        team,
        branch
      }
    });
    setTotalCounts(res.data.total);
  };

  /**
* Fetch filter values from server
*/
  const getContactsFilter = async () => {
    axios.defaults.headers.common["x-access-token"] =
      authToken
    const res = await axios.post(url + "/leads/MasterFilterValues", {});
    if (res.data[0]) {
      dispatcher(
        setFilterObject({
          ...res.data[0],
          'lead_type': ['Data', 'Leads'],
          ...booleanObject,
        })
      );
    } else {
      dispatcher(setFilterObject({}));
    }
  };
  useEffect(() => {
    if (user === undefined) {
      return;
    }
    if (authToken) {
      getContactsFilter();
    }
  }, [user, authToken]);

  useEffect(() => {
    let contact: any[] = [];
    Object.keys(contacts.data).forEach((key) => {
      contact = [...contact, ...contacts.data[key]];
    });
    setAllContacts(contact);
  }, [contacts.data]);

  useEffect(() => {
    let mapReportingTo: { [key: string]: string } = {};
    if (organizationUsers) {
      organizationUsers.forEach((item: any) => {
        if (mapReportingTo[item.uid] === undefined) {
          mapReportingTo[item.uid] = item.reporting_to;
        }
      });
    }

    if (allContacts) {
      allContacts.forEach((item: any) => {
        if (item.uid === "") {
          item["reporting_to"] = "";
        } else {
          item["reporting_to"] = mapReportingTo[item.uid]
            ? mapReportingTo[item.uid]
            : "";
        }
      });
    }

    // eslint-disable-next-line
  }, [allContacts]);

  const setUsersList = () => {
    if (role === "Lead Manager") {
      organizationUsers.map((item: any) => {
        if (usersList.includes(item)) {
          return;
        } else {
          if (item.status === "ACTIVE") {
            usersList.push(item);
          }
        }
      });
    } else {
      createUsersList(user.user_email, organizationUsers, false);
    }
  };

  useEffect(() => {
    if (selectedRows.length > 0) {
      setOwner(true);
    }
    else if (selectedRows.length === 0) {
      setOwner(false);
    }
  }, [selectedRows]);

  const clearSelectedRowsData = () => {
    setSelectedRowsData([]);
    setSelectedRows([]);
  };

  // ================================
  // Export Selected Rows
  // ================================
  // Returns array formatted for Excel export
  const exportFile = () => {
    let data: any[] = [];
    if (selectedRowsData.length === 0) {
    } else {
      selectedRowsData.forEach((item, index) => {

        //Fetching Notes from firebase Code Start
        firestore
          .collection("contactResources")
          .doc(item.contactId)
          .onSnapshot((resources) => {
            if (resources) {
              const rData = resources.data();
              if (rData?.notes) {
                let allNotes: any = "";
                rData.notes.map((Mnote: any, noteIndex: any) => {

                  //add multiple notes
                  if (Mnote.note) {
                    let str: any = Mnote.note;
                    let str1: any = Mnote.userEmail;
                    let updatedNote = str.replace(/[\r\n]+/gm, " ");
                    let updatedEmail
                    { str1 ? updatedEmail = str1.replace(/[\r\n]+/gm, " ") : updatedEmail = '' }
                    { str1 ? allNotes += updatedNote + " ( " + updatedEmail + " )  " + "||  " : allNotes += updatedNote + " , " }
                  } else {
                    allNotes += "";
                  }
                });

                //adding Notes in selectedRowsData array
                item["Notes"] = allNotes;
              } else {
                item["Notes"] = "";
              }
            }
          })
        interface DataObj {
          label: any;
          value: any;
        }
        const dbData = conatctHeader;
        const foundCustomerName: any = dbData.find((obj: DataObj) => obj.label === "customer_name")?.value;
        const foundCountryCode: any = dbData.find((obj: DataObj) => obj.label === "country_code")?.value;
        const foundMobileNo: any = dbData.find((obj: DataObj) => obj.label === "contact_no")?.value;
        const foundAlternateMobileNo: any = dbData.find((obj: DataObj) => obj.label === "alternate_no")?.value;
        const foundEmailID: any = dbData.find((obj: DataObj) => obj.label === "email")?.value;
        const foundStage: any = dbData.find((obj: DataObj) => obj.label === "stage")?.value;
        const foundOwner: any = dbData.find((obj: DataObj) => obj.label === "contact_owner_email")?.value;
        const foundLeadType: any = dbData.find((obj: DataObj) => obj.label === "lead_type")?.value;
        const foundPropertyType: any = dbData.find((obj: DataObj) => obj.label === "property_type")?.value;
        const foundPropertySubType: any = dbData.find((obj: DataObj) => obj.label === "property_sub_type")?.value;
        const foundPropertyStage: any = dbData.find((obj: DataObj) => obj.label === "property_stage")?.value;
        const foundLocation: any = dbData.find((obj: DataObj) => obj.label === "location")?.value;
        const foundProject: any = dbData.find((obj: DataObj) => obj.label === "project")?.value;
        const foundNotInterestedReason: any = dbData.find((obj: DataObj) => obj.label === "not_int_reason")?.value;
        const foundLostreason: any = dbData.find((obj: DataObj) => obj.label === "lost_reason")?.value;
        const foundOtherNotInterestedReason: any = dbData.find((obj: DataObj) => obj.label === "other_not_int_reason")?.value;
        const foundOtherLostReason: any = dbData.find((obj: DataObj) => obj.label === "other_lost_reason")?.value;
        const foundNextfollowuptype: any = dbData.find((obj: DataObj) => obj.label === "next_follow_up_type")?.value;
        const foundNextFollowupDateAndTime: any = dbData.find((obj: DataObj) => obj.label === "next_follow_up_date_time")?.value;
        const foundAddset: any = dbData.find((obj: DataObj) => obj.label === "addset")?.value;
        const foundCampaign: any = dbData.find((obj: DataObj) => obj.label === "campaign")?.value;
        const foundBudget: any = dbData.find((obj: DataObj) => obj.label === "budget")?.value;
        const foundLeadSource: any = dbData.find((obj: DataObj) => obj.label === "lead_source")?.value;
        const foundCreatedBy: any = dbData.find((obj: DataObj) => obj.label === "created_by")?.value;
        const foundCreatedAt: any = dbData.find((obj: DataObj) => obj.label === "created_at")?.value;
        const foundLeadAssignAt: any = dbData.find((obj: DataObj) => obj.label === "lead_assign_time")?.value;
        const foundFieldOne: any = dbData.find((obj: DataObj) => obj.label === "field_one")?.value;
        const foundFieldTwo: any = dbData.find((obj: DataObj) => obj.label === "field_two")?.value;
        const foundFieldThree: any = dbData.find((obj: DataObj) => obj.label === "field_three")?.value;
        const foundFieldFour: any = dbData.find((obj: DataObj) => obj.label === "field_four")?.value;
        const foundFieldFive: any = dbData.find((obj: DataObj) => obj.label === "field_five")?.value;
        const foundFieldSix: any = dbData.find((obj: DataObj) => obj.label === "field_six")?.value;
        const foundCallBackReason: any = dbData.find((obj: DataObj) => obj.label === "call_back_reason")?.value;
        const foundPreviousOwner: any = dbData.find((obj: DataObj) => obj.label === "previous_owner")?.value;
        const foundTransferStatus: any = dbData.find((obj: DataObj) => obj.label === "transfer_status")?.value;
        const foundTransferReason: any = dbData.find((obj: DataObj) => obj.label === "transfer_reason")?.value;
        const foundAssociateStatus: any = dbData.find((obj: DataObj) => obj.label === "associate_status")?.value;
        const foundSourceStatus: any = dbData.find((obj: DataObj) => obj.label === "source_status")?.value;
        const foundPreviousOwner1: any = dbData.find((obj: DataObj) => obj.label === "previous_owner_1")?.value;
        const foundPreviousOwner2: any = dbData.find((obj: DataObj) => obj.label === "previous_owner_2")?.value;
        const foundPreviousStage1: any = dbData.find((obj: DataObj) => obj.label === "previous_stage_1")?.value;
        const foundPreviousStage2: any = dbData.find((obj: DataObj) => obj.label === "previous_stage_2")?.value;
        const foundTransferBy1: any = dbData.find((obj: DataObj) => obj.label === "transfer_by_1")?.value;
        const foundTransferBy2: any = dbData.find((obj: DataObj) => obj.label === "transfer_by_2")?.value;
        const foundStageChangeAt: any = dbData.find((obj: DataObj) => obj.label === "stage_change_at")?.value;
        const foundCallResponseTime: any = dbData.find((obj: DataObj) => obj.label === "call_response_time")?.value;

        //Fetching Notes from firebase code End
        data.push({
          [foundCustomerName]: item.customer_name,
          [foundCountryCode]: item.country_code,
          [foundMobileNo]: item.contact_no,
          [foundAlternateMobileNo]: item.alternate_no,
          [foundEmailID]: item.email,
          [foundStage]: item.stage,
          [foundOwner]: item.contact_owner_email,
          "Reporting To": item?.reporting_to,
          "Branch": item?.branch,
          "Team": item?.team,
          [foundLeadType]: item?.lead_type,
          [foundCallBackReason]: item.call_back_reason,
          [foundPropertyType]: item.property_type,
          [foundPropertySubType]: item.property_sub_type,
          [foundPropertyStage]: item.property_stage,
          [foundLocation]: item.location,
          [foundProject]: item.project,
          [foundNotInterestedReason]: item.not_int_reason,
          [foundLostreason]: item.lost_reason,
          [foundOtherNotInterestedReason]: item.other_not_int_reason,
          [foundOtherLostReason]: item.other_lost_reason,
          [foundPreviousOwner]: item.previous_owner,
          [foundNextfollowuptype]: item.next_follow_up_type,
          [foundNextFollowupDateAndTime]: getDateImportString(
            item.next_follow_up_date_time
          ),
          [foundAddset]: item.addset,
          [foundCampaign]: item.campaign,
          [foundBudget]: item.budget,
          [foundLeadSource]: item.lead_source,
          [foundTransferStatus]: item.transfer_status,
          [foundTransferReason]: item.transfer_reason,
          [foundAssociateStatus]: item.associate_status,
          [foundSourceStatus]: item.source_status,
          [foundPreviousOwner1]: item.previous_owner_1,
          [foundPreviousOwner2]: item.previous_owner_2,
          [foundPreviousStage1]: item.previous_stage_1,
          [foundPreviousStage2]: item.previous_stage_2,
          [foundTransferBy1]: item.transfer_by_1,
          [foundTransferBy2]: item.transfer_by_2,
          "Modified At": getDateImportString(item.stage_change_at),
          [foundStageChangeAt]: getDateImportString(item.stage_change_at),
          [foundCreatedBy]: item.created_by,
          [foundCreatedAt]: getDateImportString(item.created_at),
          [foundLeadAssignAt]: getDateImportString(item.lead_assign_time),
          [foundCallResponseTime]: getDateImportString(item.call_response_time),
          [foundFieldOne]: item.field_one,
          [foundFieldTwo]: item.field_two,
          [foundFieldThree]: item.field_three,
          [foundFieldFour]: item.field_four,
          [foundFieldFive]: item.field_five,
          [foundFieldSix]: item.field_six,
          "Notes": item?.Notes,
        });
      });
    }
    return data;
  };

  exportFile();

  /**
* Theme styles
*/
  const topBarStyles = { backgroundColor: theme ? themeColors.backgroundColor : themeColors.backgroundColorDark, width: "100%" }
  const parentStyles = { backgroundColor: theme ? themeColors.backgroundColor : themeColors.backgroundColorDark }

  // ================================
  // Render
  // ================================
  return (
    <>
      <div className={styles.parent} style={{ display: "block", marginTop: isMobile ? "6%" : "4%", ...parentStyles }}>
        {load === true && <Loading />}
        <div className={commonStyle.topBar} style={{ marginTop: "0px", ...topBarStyles }}>

          {/* Top Action Bar */}
          <ApiTopBar
            history={history}
            title={"Add Contacts"}
            path={"/addContacts"}
            onClick={() => {
              setShowImportModal(true);
            }}
            onChange={(val) => setsearchedItem(val)}
            owner={owner}
            deleteSelected={() => {
              setDeletePopop(true);
            }}
            changeOwner={() => {
              if (selectedRowsData.length > 200) {
                dispatcher(
                  showSnackbarAction(
                    'You can transfer a maximum of 200 records',
                    'error'
                  )
                );
              } else {
                setUsersList();
                setShow(true);
              }
            }}

            filterText={"Status"}
            setColumnModal={(data) => setColumnModal(data)}
            show={true}
            showStatusBox={true}
            onExport={exportFile}
            setBulkEdit={() => {
              setShowBulkEdit(true);
            }}
            onCallLogsImport={() => {
              setCallLogsImportModal(true);
            }}
            onNotesImport={() => {
              setNotesImportModal(true);
            }}
            onTasksImport={() => {
              setTasksImportModal(true);
            }}
            searchedString={searchString}
            setAllLeads={(data) => setAllLeads(data)}
            transferLeads={transferStatusTrueRows}
            clearFilter={handleClearFilter}
            role={"Admin"}
          />
        </div>
        <div className={commonStyle.parent} style={{ minHeight: "450px" }}>

          {/* Data Table */}
          <ApiCustomTable
            tableColumns={contactColumns}
            tableRows={filterData}
            selectedRows={selectedRows}
            setSelectedRows={(data) => setSelectedRows(data)}
            tableType="User"
            showColumnModal={columnModal}
            hideColumnModal={() => setColumnModal(false)}
            selectedRowsData={selectedRowsData}
            setSelectedRowsData={(data) => setSelectedRowsData(data)}
            setPage={(val) => setPage(val)}
            setPageSize={(val) => setPageSize(val)}
            callApi={callApi}
            pageSize={pageSize}
            page={page}
            isFinished={isFinished}
            totalCount={totalCounts}
            lastPage={lastPage}
            clearFilterTable={clearFilterTable}
          />
          {show && (
            <OwnerModal
              open={show}
              close={() => {
                setShow(false);
              }}
              usersList={usersList}
              rowsSelected={selectedRowsData}
              clearSelectedRowsData={clearSelectedRowsData}
              organization_id={user.organization_id}
              freshFilterValue={freshFilterValue}

            />
          )}
          {deletePopup && (
            <DeleteSelectedModal
              data={selectedRowsData}
              clearSelectedRowsData={clearSelectedRowsData}
              open={deletePopup}
              history={history}
              close={() => {
                setDeletePopop(false);
              }}
            />
          )}
          {showImportModal && (
            <MasterContactModal
              open={showImportModal}
              close={() => setShowImportModal(false)}
              history={history}
            />
          )}
          {callLogsImportModal && (
            <ImportCallLogsModal
              open={callLogsImportModal}
              close={() => setCallLogsImportModal(false)}
              organization_id={user.organization_id}
              usersList={organizationUsers}
            />
          )}

          {notesImportModal && (
            <ImportNotesModal
              open={notesImportModal}
              close={() => setNotesImportModal(false)}
            />
          )}
          {tasksImportModal && (
            <ImportTasksModal
              open={tasksImportModal}
              close={() => setTasksImportModal(false)}
              organization_id={user.organization_id}
              usersList={organizationUsers}
              user={user}
            />
          )}
          {showBulkEdit && (
            <BulkEditModal
              open={showBulkEdit}
              organization_id={user.organization_id}
              close={() => {
                setShowBulkEdit(false);
                clearSelectedRowsData();
              }}
              rowsData={selectedRowsData}
            />
          )}
        </div>
      </div>
    </>
  );
};

// ================================
// Redux Mapping
// ================================
const mapStateToProps = (state: any) => {
  return {
    user: state.user.data,
    role: state.user.role,
    contacts: state.contacts,
    organizationUsers: state.organizationUsers.data,
    teamLeadUsers: state.teamLeadUsers.data,
    filterObject: state.filter,
    filterSort: state.filterSort,
    refreshContacts: state.refresh.refreshContacts,
    searchString: state.searchItem.contactsSearchString,
    theme: state.theme.isLightMode,
    authToken: state.user.authToken,
    clerFilterStatus: state.user.filterClearStatus,
    deleteLeadsStatus: state.deleteLeadsStatusData.deleteLeadsStatus,
    organizationIdNameData: state.organizationIdNameData.data,
  };
};

export default connect(mapStateToProps)(UserPanelSuperAdmin);
